import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/api';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiDownload,
  FiEdit2,
  FiEye,
  FiFileText,
  FiFilter,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiTrendingUp,
  FiUsers,
  FiX
} from 'react-icons/fi';
import zovcurLogo from '../../assets/logo/logo.jpg';
import { generateZovcurInvoicePdf } from '../../utils/generateZovcurInvoicePdf';
import {
  deleteInvoice as deleteInvoiceApi,
  fetchAllInvoices,
  purgeLocalInvoiceStorage,
  updateInvoicePayment
} from '../../utils/zovcurInvoiceApi';
import {
  applyInvoiceCompanyDefaults,
  computeInvoiceTotals,
  formatProductName,
  getInvoiceStateLabel,
  INDIAN_STATES
} from '../../utils/zovcurInvoiceStore';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const formatCompactMoney = (value) => {
  const n = Number(value) || 0;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return formatMoney(n);
};

const normalizeSalesmanName = (name) => String(name || '').trim().toLowerCase();

const getBillAmount = (inv) => {
  const totals = computeInvoiceTotals(inv?.products || []);
  return Number(totals.roundedNetAmount) || 0;
};

const getPaymentHistory = (inv) => {
  const history = Array.isArray(inv?.paymentHistory) ? inv.paymentHistory : [];
  if (history.length > 0) return history;
  const paid = Number(inv?.paidAmount) || 0;
  if (paid > 0) {
    return [
      {
        id: 'legacy',
        amount: paid,
        paidAt: inv?.paidAt || null,
        note: 'Previous payment',
        method: ''
      }
    ];
  }
  return [];
};

const getPaidAmount = (inv) => {
  const history = getPaymentHistory(inv);
  if (history.length > 0) {
    return history.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }
  const paid = Number(inv?.paidAmount);
  if (!Number.isNaN(paid) && paid > 0) return paid;
  if ((inv?.paymentStatus || 'Unpaid') === 'Paid') return getBillAmount(inv);
  return 0;
};

const getBalanceAmount = (inv) => Math.max(0, getBillAmount(inv) - getPaidAmount(inv));

const resolvePaymentLabel = (inv) => {
  const status = inv?.paymentStatus || 'Unpaid';
  if (status === 'Paid' || status === 'Partially Paid' || status === 'Unpaid') return status;
  const paid = getPaidAmount(inv);
  const bill = getBillAmount(inv);
  if (paid <= 0) return 'Unpaid';
  if (bill > 0 && paid >= bill) return 'Paid';
  if (paid > 0) return 'Partially Paid';
  return 'Unpaid';
};

const paymentBadgeClass = (status) => {
  if (status === 'Paid') return 'bg-emerald-50 text-emerald-700';
  if (status === 'Partially Paid') return 'bg-sky-50 text-sky-800';
  return 'bg-amber-50 text-amber-800';
};

const StatCard = ({ label, value, sub, icon: Icon, tone = 'navy' }) => {
  const tones = {
    navy: { value: 'text-[#1a365d]', icon: 'bg-[#e8f0f4] text-[#1a365d]' },
    teal: { value: 'text-teal-700', icon: 'bg-teal-50 text-teal-700' },
    amber: { value: 'text-amber-700', icon: 'bg-amber-50 text-amber-700' },
    emerald: { value: 'text-emerald-700', icon: 'bg-emerald-50 text-emerald-700' },
    rose: { value: 'text-rose-700', icon: 'bg-rose-50 text-rose-700' }
  };
  const t = tones[tone] || tones.navy;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </p>
          <p className={`mt-1 text-xl sm:text-2xl font-bold truncate ${t.value}`}>{value}</p>
          {sub ? <p className="text-[10px] sm:text-xs text-slate-400 mt-1 truncate">{sub}</p> : null}
        </div>
        <div
          className={`shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${t.icon}`}
        >
          <Icon className="text-base sm:text-lg" />
        </div>
      </div>
    </div>
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [payAmountInput, setPayAmountInput] = useState('');
  const [payNoteInput, setPayNoteInput] = useState('');
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [stateFilter, setStateFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [salesmanFilter, setSalesmanFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [salesmen, setSalesmen] = useState([]);

  const fetchSalesmen = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API_ENDPOINTS.getSalesmen, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesmen(response.data || []);
    } catch {
      setSalesmen([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      purgeLocalInvoiceStorage();
      const [data] = await Promise.all([fetchAllInvoices(), fetchSalesmen()]);
      setInvoices(data);
    } catch {
      alert('Failed to load invoices from database.');
    } finally {
      setLoading(false);
    }
  }, [fetchSalesmen]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredInvoices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (stateFilter !== 'all' && getInvoiceStateLabel(inv) !== stateFilter) return false;
      const status = resolvePaymentLabel(inv);
      if (paymentFilter !== 'all' && status !== paymentFilter) return false;
      if (salesmanFilter !== 'all') {
        const invSalesman = normalizeSalesmanName(inv.invoiceDetails?.salesmanName);
        if (invSalesman !== normalizeSalesmanName(salesmanFilter)) return false;
      }
      if (q) {
        const haystack = [
          inv.invoiceNumber,
          inv.invoiceDetails?.billNo,
          inv.customer?.doctor,
          inv.invoiceDetails?.salesmanName,
          getInvoiceStateLabel(inv),
          inv.invoiceDetails?.billType
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [invoices, stateFilter, paymentFilter, salesmanFilter, searchTerm]);

  const salesmanCounts = useMemo(() => {
    const counts = {};
    invoices.forEach((inv) => {
      const name = String(inv.invoiceDetails?.salesmanName || '').trim();
      if (!name) return;
      counts[normalizeSalesmanName(name)] = (counts[normalizeSalesmanName(name)] || 0) + 1;
    });
    return counts;
  }, [invoices]);

  const salesmanOptions = useMemo(() => {
    const fromMaster = salesmen
      .map((s) => String(s.name || '').trim())
      .filter(Boolean);
    const fromInvoices = invoices
      .map((inv) => String(inv.invoiceDetails?.salesmanName || '').trim())
      .filter(Boolean);
    const unique = [...new Set([...fromMaster, ...fromInvoices])];
    return unique.sort((a, b) => a.localeCompare(b));
  }, [salesmen, invoices]);

  const stateCounts = useMemo(() => {
    const counts = {};
    invoices.forEach((inv) => {
      const label = getInvoiceStateLabel(inv);
      if (label !== '—') {
        counts[label] = (counts[label] || 0) + 1;
      }
    });
    return counts;
  }, [invoices]);

  const stats = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    let totalAmount = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;
    let paidCount = 0;
    let unpaidCount = 0;
    let partialCount = 0;
    let collectedAmount = 0;
    let balanceDue = 0;
    let totalQty = 0;
    let totalGst = 0;
    let thisMonth = 0;
    let thisMonthAmount = 0;
    const states = new Set();
    const customers = new Set();

    filteredInvoices.forEach((inv) => {
      const totals = computeInvoiceTotals(inv.products);
      const amount = totals.roundedNetAmount;
      totalAmount += amount;
      totalQty += totals.quantity;
      totalGst += totals.gstTotal;

      const status = resolvePaymentLabel(inv);
      const paid = getPaidAmount(inv);
      const balance = Math.max(0, amount - paid);
      collectedAmount += paid;
      balanceDue += balance;

      if (status === 'Paid') {
        paidAmount += amount;
        paidCount += 1;
      } else if (status === 'Partially Paid') {
        partialCount += 1;
        unpaidAmount += balance;
        unpaidCount += 1;
      } else {
        unpaidAmount += amount;
        unpaidCount += 1;
      }

      const billDate = inv.invoiceDetails?.billDate
        ? new Date(inv.invoiceDetails.billDate)
        : null;
      if (
        billDate &&
        !Number.isNaN(billDate.getTime()) &&
        billDate.getMonth() === month &&
        billDate.getFullYear() === year
      ) {
        thisMonth += 1;
        thisMonthAmount += amount;
      }

      const state = getInvoiceStateLabel(inv);
      if (state !== '—') states.add(state);

      const customer = inv.customer?.doctor;
      if (customer) customers.add(customer);
    });

    return {
      count: filteredInvoices.length,
      totalAmount,
      paidAmount,
      unpaidAmount,
      paidCount,
      unpaidCount,
      partialCount,
      collectedAmount,
      balanceDue,
      totalQty,
      totalGst,
      thisMonth,
      thisMonthAmount,
      statesCount: states.size,
      customersCount: customers.size
    };
  }, [filteredInvoices]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (searchTerm.trim()) n += 1;
    if (stateFilter !== 'all') n += 1;
    if (paymentFilter !== 'all') n += 1;
    if (salesmanFilter !== 'all') n += 1;
    return n;
  }, [searchTerm, stateFilter, paymentFilter, salesmanFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('all');
    setPaymentFilter('all');
    setSalesmanFilter('all');
  };

  const topStates = useMemo(
    () =>
      Object.entries(stateCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
    [stateCounts]
  );

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoiceDetails?.billNo || inv.invoiceNumber}?`)) return;
    try {
      await deleteInvoiceApi(inv.id);
      await refresh();
      if (viewInvoice?.id === inv.id) setViewInvoice(null);
    } catch {
      alert('Failed to delete invoice.');
    }
  };

  const handleEdit = (inv) => {
    navigate(`/invoices?edit=${inv.id}`);
  };

  const handleMarkPayment = async (inv, payload) => {
    if (!inv?.id) return;
    setPaymentSaving(true);
    try {
      const updated = await updateInvoicePayment(inv.id, payload);
      const nextStatus = updated.paymentStatus || resolvePaymentLabel({ ...inv, ...updated });
      const nextPaid = Number(updated.paidAmount);
      const nextHistory = Array.isArray(updated.paymentHistory)
        ? updated.paymentHistory
        : inv.paymentHistory || [];
      setInvoices((prev) =>
        prev.map((item) =>
          item.id === inv.id
            ? {
                ...item,
                paymentStatus: nextStatus,
                paidAmount: Number.isNaN(nextPaid)
                  ? getPaidAmount({ ...item, paymentStatus: nextStatus, paymentHistory: nextHistory })
                  : nextPaid,
                paymentHistory: nextHistory,
                paidAt:
                  updated.paidAt ??
                  (nextStatus === 'Unpaid' ? null : new Date().toISOString()),
                ...(Array.isArray(updated.products) && updated.products.length > 0
                  ? {
                      customer: updated.customer,
                      invoiceDetails: updated.invoiceDetails,
                      products: updated.products,
                      bankDetails: updated.bankDetails,
                      company: updated.company,
                      invoiceNumber: updated.invoiceNumber
                    }
                  : {})
              }
            : item
        )
      );
      setViewInvoice((prev) => {
        if (!prev || prev.id !== inv.id) return prev;
        return {
          ...prev,
          paymentStatus: nextStatus,
          paidAmount: Number.isNaN(nextPaid)
            ? getPaidAmount({ ...prev, paymentStatus: nextStatus, paymentHistory: nextHistory })
            : nextPaid,
          paymentHistory: nextHistory,
          paidAt:
            updated.paidAt ?? (nextStatus === 'Unpaid' ? null : new Date().toISOString())
        };
      });
      setPayAmountInput('');
      setPayNoteInput('');
      setEditingPaymentId(null);
    } catch {
      alert('Failed to update payment.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleSavePaymentAmount = async () => {
    if (!viewInvoice) return;
    const entered = Number(payAmountInput);
    if (Number.isNaN(entered) || entered <= 0) {
      alert('Enter a valid paid amount greater than 0.');
      return;
    }
    if (editingPaymentId) {
      await handleMarkPayment(viewInvoice, {
        action: 'edit',
        recordId: editingPaymentId,
        amount: entered,
        note: payNoteInput
      });
      return;
    }
    await handleMarkPayment(viewInvoice, {
      action: 'add',
      amount: entered,
      note: payNoteInput
    });
  };

  const startEditPayment = (record) => {
    setEditingPaymentId(record.id);
    setPayAmountInput(String(record.amount || ''));
    setPayNoteInput(record.note || '');
  };

  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setPayAmountInput('');
    setPayNoteInput('');
  };

  const handleDeletePaymentRecord = async (recordId) => {
    if (!viewInvoice || !recordId) return;
    if (!window.confirm('Delete this payment record?')) return;
    await handleMarkPayment(viewInvoice, { action: 'delete', recordId });
  };

  const openViewInvoice = (inv) => {
    setViewInvoice(inv);
    setPayAmountInput('');
    setPayNoteInput('');
    setEditingPaymentId(null);
  };

  const handleDownloadPdf = async (inv) => {
    setPdfLoadingId(inv.id);
    try {
      await generateZovcurInvoicePdf(applyInvoiceCompanyDefaults(inv), zovcurLogo);
    } catch {
      alert('Failed to generate PDF.');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const viewTotals = viewInvoice ? computeInvoiceTotals(viewInvoice.products) : null;

  return (
    <div className="min-h-screen bg-[#f4f7fa] p-3 sm:p-4 md:p-6 font-[Montserrat,sans-serif]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-start justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-2">
              <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-[#1a365d] text-white shrink-0">
                <FiFileText />
              </span>
              Orders
            </h1>
            <p className="text-slate-500 mt-1 text-xs sm:text-sm">
              Zovcur Pharma invoices — track payments, download PDFs, and manage bills
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => refresh()}
              className="flex-1 sm:flex-none px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 shadow-sm text-sm font-semibold"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="flex-1 sm:flex-none px-4 py-2.5 min-h-[44px] rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center justify-center gap-2 shadow-md text-sm font-semibold"
            >
              <FiPlus /> New Invoice
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5 sm:gap-3 mb-4">
          <StatCard
            label="Invoices"
            value={stats.count}
            sub={activeFilterCount ? 'Filtered view' : `${invoices.length} overall`}
            icon={FiFileText}
          />
          <StatCard
            label="Total Amount"
            value={formatCompactMoney(stats.totalAmount)}
            sub={formatMoney(stats.totalAmount)}
            icon={FiTrendingUp}
            tone="teal"
          />
          <StatCard
            label="Received"
            value={formatCompactMoney(stats.collectedAmount)}
            sub={`${stats.paidCount} paid · ${stats.partialCount || 0} partial`}
            icon={FiCheckCircle}
            tone="emerald"
          />
          <StatCard
            label="Pending"
            value={formatCompactMoney(stats.balanceDue)}
            sub={`${stats.unpaidCount} due · balance`}
            icon={FiAlertCircle}
            tone="amber"
          />
          <StatCard
            label="This Month"
            value={stats.thisMonth}
            sub={formatCompactMoney(stats.thisMonthAmount)}
            icon={FiCalendar}
            tone="teal"
          />
          <StatCard
            label="Customers"
            value={stats.customersCount}
            sub={`${stats.statesCount} state${stats.statesCount !== 1 ? 's' : ''} · ${stats.totalQty} strips`}
            icon={FiUsers}
          />
        </div>

        {/* Top states */}
        {topStates.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FiMapPin className="text-teal-600" />
              <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Top States
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {topStates.map(([state, count]) => {
                const pct = invoices.length ? Math.round((count / invoices.length) * 100) : 0;
                const active = stateFilter === state;
                return (
                  <button
                    key={state}
                    type="button"
                    onClick={() => setStateFilter(active ? 'all' : state)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? 'bg-[#1a365d] text-white border-[#1a365d]'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50'
                    }`}
                  >
                    <span>{state}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        active ? 'bg-white/20' : 'bg-teal-100 text-teal-800'
                      }`}
                    >
                      {count} · {pct}%
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search / filters */}
        <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm mb-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-3">
            <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search invoice, doctor, salesman…"
                className="w-full pl-10 pr-3 py-2.5 min-h-[44px] rounded-xl border border-slate-200 bg-slate-50 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={`flex-1 sm:flex-none px-4 py-2.5 min-h-[44px] rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 ${
                  showFilters || activeFilterCount
                    ? 'border-teal-300 bg-teal-50 text-teal-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <FiFilter /> Filters
                {activeFilterCount > 0 ? (
                  <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#1a365d] px-1.5 text-[11px] text-white">
                    {activeFilterCount}
                  </span>
                ) : null}
              </button>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-3 py-2.5 min-h-[44px] rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-1"
                >
                  <FiX /> Clear
                </button>
              )}
            </div>
          </div>

          {showFilters ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Salesman</label>
                <select
                  value={salesmanFilter}
                  onChange={(e) => setSalesmanFilter(e.target.value)}
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All Salesmen ({invoices.length})</option>
                  {salesmanOptions.map((name) => (
                    <option key={name} value={name}>
                      {name} ({salesmanCounts[normalizeSalesmanName(name)] || 0})
                    </option>
                  ))}
                </select>
                {salesmanOptions.length === 0 ? (
                  <p className="text-[11px] text-slate-400 mt-1">
                    No salesmen yet.{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/salesman')}
                      className="text-teal-700 font-semibold hover:underline"
                    >
                      Add in Salesman
                    </button>
                  </p>
                ) : null}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">State</label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All States ({invoices.length})</option>
                  {INDIAN_STATES.filter((s) => stateCounts[s.name]).map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name} ({stateCounts[s.name]})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Payment</label>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full px-3 py-2.5 min-h-[44px] rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Partially Paid">Partially Paid</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </div>
          ) : null}
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-xs sm:text-sm text-slate-600">
              Showing <strong className="text-[#1a365d]">{filteredInvoices.length}</strong> of{' '}
              <strong>{invoices.length}</strong> invoices
            </p>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden">
            {loading ? (
              <div className="py-16 text-center text-slate-500">
                <div className="inline-block animate-spin rounded-full h-9 w-9 border-b-2 border-[#1a365d] mb-3" />
                <p className="text-sm">Loading invoices…</p>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="py-16 text-center text-slate-500 px-4 text-sm">
                {invoices.length === 0
                  ? 'No invoices yet. Create one from New Invoice.'
                  : 'No invoices match your search or filters.'}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredInvoices.map((inv) => {
                  const totals = computeInvoiceTotals(inv.products);
                  const customerLabel = inv.customer?.doctor || '—';
                  const stateLabel = getInvoiceStateLabel(inv);
                  const payStatus = resolvePaymentLabel(inv);
                  return (
                    <div key={inv.id} className="p-3.5 active:bg-teal-50/40">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-bold text-[#1a365d] text-base truncate">
                            {inv.invoiceNumber}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {formatDate(inv.invoiceDetails?.billDate)}
                            {stateLabel && stateLabel !== '—' ? ` · ${stateLabel}` : ''}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${paymentBadgeClass(
                            payStatus
                          )}`}
                        >
                          {payStatus}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 truncate">{customerLabel}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        Salesman:{' '}
                        <span className="text-slate-700 font-medium">
                          {String(inv.invoiceDetails?.salesmanName || '').trim() || '—'}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Paid {formatMoney(getPaidAmount(inv))} · Balance{' '}
                        <span className="font-semibold text-amber-800">
                          {formatMoney(getBalanceAmount(inv))}
                        </span>
                      </p>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <p className="text-base font-bold text-[#1a365d]">
                          {formatMoney(totals.roundedNetAmount)}
                        </p>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleDownloadPdf(inv)}
                            disabled={pdfLoadingId === inv.id}
                            className="p-2.5 min-h-[44px] min-w-[44px] rounded-xl text-teal-600 hover:bg-teal-50 disabled:opacity-50 flex items-center justify-center"
                            title="Download PDF"
                            aria-label="Download PDF"
                          >
                            <FiDownload size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openViewInvoice(inv)}
                            className="p-2.5 min-h-[44px] min-w-[44px] rounded-xl text-[#1a365d] hover:bg-slate-100 flex items-center justify-center"
                            title="View"
                            aria-label="View"
                          >
                            <FiEye size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(inv)}
                            className="p-2.5 min-h-[44px] min-w-[44px] rounded-xl text-emerald-600 hover:bg-emerald-50 flex items-center justify-center"
                            title="Edit"
                            aria-label="Edit"
                          >
                            <FiEdit2 size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(inv)}
                            className="p-2.5 min-h-[44px] min-w-[44px] rounded-xl text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                            title="Delete"
                            aria-label="Delete"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#1a365d] text-white">
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Invoice
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Doctor / Customer
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Salesman
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    State
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Amount
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Payment
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-slate-500">
                      <div className="inline-block animate-spin rounded-full h-9 w-9 border-b-2 border-[#1a365d] mb-3" />
                      <p className="text-sm">Loading invoices…</p>
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-slate-500">
                      {invoices.length === 0
                        ? 'No invoices yet. Create one from New Invoice.'
                        : 'No invoices match your search or filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv, index) => {
                    const totals = computeInvoiceTotals(inv.products);
                    const customerLabel = inv.customer?.doctor || '—';
                    const stateLabel = getInvoiceStateLabel(inv);
                    const payStatus = resolvePaymentLabel(inv);
                    return (
                      <tr
                        key={inv.id}
                        className={`border-b border-slate-100 hover:bg-teal-50/40 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="font-semibold text-[#1a365d]">
                            {inv.invoiceNumber}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                          {formatDate(inv.invoiceDetails?.billDate)}
                        </td>
                        <td className="px-3 py-3 text-slate-800 font-medium min-w-[140px]">
                          {customerLabel}
                        </td>
                        <td className="px-3 py-3 text-slate-700 whitespace-nowrap">
                          {String(inv.invoiceDetails?.salesmanName || '').trim() || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{stateLabel}</td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                          {inv.invoiceDetails?.billType || '—'}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">
                          <div>{formatMoney(totals.roundedNetAmount)}</div>
                          {payStatus !== 'Unpaid' ? (
                            <div className="text-[11px] font-normal text-slate-500 mt-0.5">
                              Paid {formatMoney(getPaidAmount(inv))} · Bal{' '}
                              {formatMoney(getBalanceAmount(inv))}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${paymentBadgeClass(
                              payStatus
                            )}`}
                          >
                            {payStatus}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleDownloadPdf(inv)}
                              disabled={pdfLoadingId === inv.id}
                              className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 disabled:opacity-50"
                              title="Download PDF"
                            >
                              <FiDownload />
                            </button>
                            <button
                              type="button"
                              onClick={() => openViewInvoice(inv)}
                              className="p-2 rounded-lg text-[#1a365d] hover:bg-slate-100"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(inv)}
                              className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(inv)}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View modal */}
      {viewInvoice && (
        <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50">
          <div className="bg-white sm:rounded-2xl shadow-2xl max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto flex flex-col">
            <div className="p-3 sm:p-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-[#1a365d] text-white sm:rounded-t-2xl shrink-0">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <h2 className="text-base sm:text-lg font-bold truncate">
                  Invoice {viewInvoice.invoiceNumber}
                </h2>
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${
                    resolvePaymentLabel(viewInvoice) === 'Paid'
                      ? 'bg-emerald-400/20 text-emerald-100'
                      : resolvePaymentLabel(viewInvoice) === 'Partially Paid'
                        ? 'bg-sky-400/20 text-sky-100'
                        : 'bg-amber-400/20 text-amber-100'
                  }`}
                >
                  {resolvePaymentLabel(viewInvoice)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setViewInvoice(null)}
                className="p-2 min-h-[44px] min-w-[44px] rounded-lg text-white/80 hover:bg-white/10 flex items-center justify-center"
                title="Close"
                aria-label="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-4 text-sm flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p>
                  <span className="text-slate-500">Bill Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.billDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Order Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.orderDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Due Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.dueDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Doctor:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.customer?.doctor || '—'}
                  </span>
                </p>
                {String(viewInvoice.customer?.gstin || '').trim() ? (
                  <p>
                    <span className="text-slate-500">GSTIN:</span>{' '}
                    <span className="font-medium text-slate-800">
                      {viewInvoice.customer.gstin}
                    </span>
                  </p>
                ) : null}
                {String(viewInvoice.customer?.dlNo || '').trim() ? (
                  <p>
                    <span className="text-slate-500">DL No:</span>{' '}
                    <span className="font-medium text-slate-800">
                      {viewInvoice.customer.dlNo}
                    </span>
                  </p>
                ) : null}
                <p>
                  <span className="text-slate-500">State:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {getInvoiceStateLabel(viewInvoice)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">State Code:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.invoiceDetails?.stateCode || '—'}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Salesman:</span>{' '}
                  <span className="font-semibold text-[#0e3d52]">
                    {String(viewInvoice.invoiceDetails?.salesmanName || '').trim() || '—'}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Delivery:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.invoiceDetails?.deliveryType || '—'}
                  </span>
                </p>
                {resolvePaymentLabel(viewInvoice) !== 'Unpaid' && viewInvoice.paidAt ? (
                  <p className="sm:col-span-2">
                    <span className="text-slate-500">Last payment:</span>{' '}
                    <span className="font-medium text-emerald-700">
                      {formatDate(viewInvoice.paidAt)}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="border rounded-xl overflow-x-auto">
                <table className="w-full text-xs min-w-[420px]">
                  <thead>
                    <tr className="bg-[#1a365d] text-white">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-center">Free</th>
                      <th className="px-3 py-2 text-right">Rate</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.products
                      .filter((p) => p.product || Number(p.qty) || Number(p.freeQty))
                      .map((p, i) => {
                        const qty = Number(p.qty) || 0;
                        const freeQty = Number(p.freeQty) || 0;
                        const rate = Number(p.rate) || 0;
                        const gst = (qty * rate * (Number(p.gstPercent) || 0)) / 100;
                        const total = qty * rate + gst;
                        return (
                          <tr key={p.id || i} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-bold uppercase text-slate-800">
                              {formatProductName(p.product)}
                            </td>
                            <td className="px-3 py-2 text-center">{p.qty || 0}</td>
                            <td className="px-3 py-2 text-center text-amber-800">
                              {freeQty || '—'}
                            </td>
                            <td className="px-3 py-2 text-right">{rate.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-semibold">
                              {total.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {viewTotals && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1.5">
                  {(viewTotals.freeQuantity > 0 || viewTotals.freeCost > 0) && (
                    <>
                      <div className="flex justify-between text-amber-800">
                        <span>Free Qty (strips)</span>
                        <span>{viewTotals.freeQuantity}</span>
                      </div>
                      <div className="flex justify-between text-amber-800">
                        <span>Free Cost (not billed)</span>
                        <span>{formatMoney(viewTotals.freeCost)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>Base Amount</span>
                    <span>{formatMoney(viewTotals.baseAmount)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>GST Total</span>
                    <span>{formatMoney(viewTotals.gstTotal)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base text-[#1a365d] pt-1 border-t border-slate-200">
                    <span>Invoice Amount</span>
                    <span>{formatMoney(viewTotals.roundedNetAmount)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-700 font-semibold">
                    <span>Amount Paid</span>
                    <span>{formatMoney(getPaidAmount(viewInvoice))}</span>
                  </div>
                  <div className="flex justify-between text-amber-800 font-semibold">
                    <span>Balance</span>
                    <span>{formatMoney(getBalanceAmount(viewInvoice))}</span>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wide text-[#1a365d]">
                  Payment history
                </p>
                <p className="text-xs text-slate-500">
                  Bill {formatMoney(getBillAmount(viewInvoice))}. Add each payment — balance and
                  status update automatically. Edit or delete any record.
                </p>

                {getPaymentHistory(viewInvoice).length === 0 ? (
                  <p className="text-sm text-slate-400 py-2">No payment records yet.</p>
                ) : (
                  <div className="border rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600">
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-left">Note</th>
                          <th className="px-3 py-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaymentHistory(viewInvoice)
                          .slice()
                          .sort(
                            (a, b) =>
                              new Date(b.paidAt || 0).getTime() - new Date(a.paidAt || 0).getTime()
                          )
                          .map((rec) => (
                            <tr
                              key={rec.id}
                              className={`border-t border-slate-100 ${
                                editingPaymentId === rec.id ? 'bg-sky-50' : 'bg-white'
                              }`}
                            >
                              <td className="px-3 py-2 whitespace-nowrap">
                                {formatDate(rec.paidAt)}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                                {formatMoney(rec.amount)}
                              </td>
                              <td className="px-3 py-2 text-slate-600">
                                {rec.note || '—'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => startEditPayment(rec)}
                                    disabled={paymentSaving}
                                    className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                                    title="Edit payment"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePaymentRecord(rec.id)}
                                    disabled={paymentSaving}
                                    className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                                    title="Delete payment"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-3 space-y-2">
                  <p className="text-xs font-semibold text-slate-600">
                    {editingPaymentId ? 'Edit payment record' : 'Add payment'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-500">Amount (₹)</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={payAmountInput}
                        onChange={(e) => setPayAmountInput(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                        placeholder="e.g. 500"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold text-slate-500">Note (optional)</span>
                      <input
                        type="text"
                        value={payNoteInput}
                        onChange={(e) => setPayNoteInput(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                        placeholder="Cash / UPI / cheque no."
                      />
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    {editingPaymentId
                      ? 'Saving will update this record.'
                      : `After add, balance ≈ ${formatMoney(
                          Math.max(
                            0,
                            getBalanceAmount(viewInvoice) - (Number(payAmountInput) || 0)
                          )
                        )}`}
                  </p>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSavePaymentAmount}
                      disabled={paymentSaving}
                      className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-60 font-semibold"
                    >
                      <FiCheckCircle />{' '}
                      {paymentSaving
                        ? 'Saving…'
                        : editingPaymentId
                          ? 'Update Payment'
                          : 'Add Payment'}
                    </button>
                    {editingPaymentId ? (
                      <button
                        type="button"
                        onClick={cancelEditPayment}
                        disabled={paymentSaving}
                        className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold"
                      >
                        Cancel edit
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        handleMarkPayment(viewInvoice, { action: 'pay_full', note: 'Full payment' })
                      }
                      disabled={paymentSaving || getBalanceAmount(viewInvoice) <= 0}
                      className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center justify-center gap-2 disabled:opacity-60 font-semibold"
                    >
                      Pay remaining
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm('Clear all payment history for this invoice?')) return;
                        handleMarkPayment(viewInvoice, { action: 'clear' });
                      }}
                      disabled={paymentSaving || getPaymentHistory(viewInvoice).length === 0}
                      className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center justify-center gap-2 disabled:opacity-60 font-semibold"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-1 pb-[env(safe-area-inset-bottom)]">
                <button
                  type="button"
                  onClick={() => handleDownloadPdf(viewInvoice)}
                  disabled={pdfLoadingId === viewInvoice.id}
                  className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl bg-teal-600 text-white hover:bg-teal-700 flex items-center justify-center gap-2 disabled:opacity-50 font-semibold"
                >
                  <FiDownload />{' '}
                  {pdfLoadingId === viewInvoice.id ? 'Generating…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewInvoice(null);
                    handleEdit(viewInvoice);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center justify-center gap-2 font-semibold"
                >
                  <FiEdit2 /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => setViewInvoice(null)}
                  className="w-full sm:w-auto px-4 py-2.5 min-h-[44px] rounded-xl border border-slate-300 hover:bg-slate-50 font-semibold text-slate-700 flex items-center justify-center"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
